import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Comment,
  TrendingUp,
  Refresh,
  Person
} from '@mui/icons-material';
import axios from 'axios';
import { getAPIUrl } from '../apiUtils';

const API_BASE_URL = getAPIUrl();

const CommentsStats = ({ selectedGroups, startDate, endDate, communities }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [communityStats, setCommunityStats] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [totalComments, setTotalComments] = useState(0);
  const [userNames, setUserNames] = useState({});

  // Функция для получения названия сообщества по ID
  const getCommunityName = (groupId) => {
    if (!communities) return groupId;
    const community = communities.find(c => c.group_id === groupId);
    return community ? community.title : groupId;
  };

  // Функция для получения имен пользователей VK
  const fetchUserNames = useCallback(async (userIds) => {
    if (!userIds || userIds.length === 0) return;
    
    try {
      const params = new URLSearchParams();
      userIds.forEach(id => params.append('user_ids', id));
      
      const response = await axios.get(
        `${API_BASE_URL}/vk/get_data_users/stored?${params.toString()}`
      );
      
      let data = response.data;
      if (response.data.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (!Array.isArray(data)) {
        data = [];
      }
      
      console.log('Данные пользователей VK:', data);
      
      const nameMap = {};
      data.forEach(user => {
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        // Если есть screen_name, добавляем его в скобках
        const displayName = fullName || `ID: ${user.id}`;
        const screenName = user.screen_name ? ` (@${user.screen_name})` : '';
        nameMap[user.id] = displayName + screenName;
      });
      
      setUserNames(nameMap);
    } catch (err) {
      console.error('Ошибка при получении имен пользователей VK:', err);
      // В случае ошибки используем ID как имя
      const fallbackMap = {};
      userIds.forEach(id => {
        fallbackMap[id] = `ID: ${id}`;
      });
      setUserNames(fallbackMap);
    }
  }, []);

  // Функция для получения статистики комментариев и топ пользователей
  const fetchCommentsStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Выбранные группы:', selectedGroups);
      console.log('Даты:', { startDate, endDate });
      
      const params = new URLSearchParams();
      
      // Если есть выбранные группы, используем их, иначе получаем данные по всем группам
      if (selectedGroups && selectedGroups.length > 0) {
        params.append('group_ids', selectedGroups.join(','));
      }
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      params.append('limit', '20'); // Топ 20 пользователей
      
      console.log('Параметры запроса:', params.toString());

      const response = await axios.get(
        `${API_BASE_URL}/influencers/activity?${params.toString()}`
      );

      let data = response.data;
      if (response.data.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (!Array.isArray(data)) {
        data = [];
      }

      // Отладочная информация
      console.log('Данные от API:', data);
      console.log('Количество записей:', data.length);
      console.log('Уникальные group_id:', [...new Set(data.map(item => item.group_id))]);
      console.log('Данные о сообществах:', communities);

      // Агрегируем данные по сообществам
      const communityMap = {};
      let total = 0;

      data.forEach(user => {
        const groupId = user.group_id;
        if (!communityMap[groupId]) {
          communityMap[groupId] = {
            group_id: groupId,
            total_comments: 0,
            user_count: 0
          };
        }
        communityMap[groupId].total_comments += user.total_comments || 0;
        communityMap[groupId].user_count += 1;
        total += user.total_comments || 0;
      });

      console.log('Агрегированные данные по сообществам:', Object.values(communityMap));

      // Сортируем пользователей по количеству комментариев и берем топ 20
      const sortedUsers = data
        .sort((a, b) => (b.total_comments || 0) - (a.total_comments || 0))
        .slice(0, 20);

      setCommunityStats(Object.values(communityMap));
      setTopUsers(sortedUsers);
      setTotalComments(total);

      // Загружаем имена пользователей для топ-20
      const userIds = sortedUsers.map(user => user.user_id);
      if (userIds.length > 0) {
        fetchUserNames(userIds);
      }

    } catch (err) {
      console.error('Ошибка при получении статистики комментариев:', err);
      setError('Не удалось загрузить статистику комментариев');
      setCommunityStats([]);
      setTopUsers([]);
      setTotalComments(0);
    } finally {
      setLoading(false);
    }
  }, [selectedGroups, startDate, endDate]);

  // Загружаем данные при изменении параметров
  useEffect(() => {
    fetchCommentsStats();
  }, [selectedGroups, startDate, endDate, fetchCommentsStats]);

  const handleRefresh = () => {
    fetchCommentsStats();
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Заголовок с кнопкой обновления */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Comment sx={{ color: '#4a90e2', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e1e2f' }}>
            {selectedGroups && selectedGroups.length > 0 
              ? `Статистика комментариев (${selectedGroups.length} сообществ)` 
              : 'Статистика комментариев (все сообщества)'}
          </Typography>
        </Box>
        <Tooltip title="Обновить данные">
          <IconButton onClick={handleRefresh} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Общая статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(145deg, #ffffff, #e6f0fa)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Comment sx={{ color: '#4a90e2', mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e1e2f' }}>
                    {totalComments.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Всего комментариев
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(145deg, #ffffff, #e6f0fa)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ color: '#00c853', mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e1e2f' }}>
                    {communityStats.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Сообществ
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(145deg, #ffffff, #e6f0fa)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Person sx={{ color: '#ff9800', mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e1e2f' }}>
                    {topUsers.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Активных пользователей
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Статистика по сообществам */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Комментарии по сообществам
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Сообщество</TableCell>
                  <TableCell align="right">Комментарии</TableCell>
                  <TableCell align="right">Пользователи</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {communityStats.map((community) => (
                  <TableRow key={community.group_id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {getCommunityName(community.group_id)}
                        </Typography>
                        <Chip 
                          label={community.group_id} 
                          size="small" 
                          sx={{ 
                            backgroundColor: '#4a90e2', 
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.75rem'
                          }} 
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {community.total_comments.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {community.user_count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Топ пользователей */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Топ-20 пользователей по комментариям
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Пользователь</TableCell>
                  <TableCell align="right">Комментарии</TableCell>
                  <TableCell align="right">Лайки</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topUsers.map((user, index) => (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      <Chip 
                        label={index + 1} 
                        size="small" 
                        sx={{ 
                          backgroundColor: index < 3 ? '#ff9800' : '#e0e0e0',
                          color: index < 3 ? 'white' : 'black',
                          fontWeight: 'bold'
                        }} 
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {userNames[user.user_id] || `ID: ${user.user_id}`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            ID: {user.user_id}
                          </Typography>
                          <Chip 
                            label={getCommunityName(user.group_id)} 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#e3f2fd', 
                              color: '#1976d2',
                              fontSize: '0.7rem',
                              height: '20px'
                            }} 
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4a90e2' }}>
                        {user.total_comments?.toLocaleString() || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: '#00c853' }}>
                        {user.total_likes?.toLocaleString() || 0}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CommentsStats;
